import * as React from 'react'
import {
    useRef,
    JSX,
    SetStateAction,
    Dispatch,
    MouseEventHandler,
    PropsWithChildren,
} from 'react'
import classnames from 'classnames'

import { useIsScrolled } from '../../../hooks/useIsScrolled/useIsScrolled'
import { Icon } from '@/components/atoms'

export type DrawerProps = React.PropsWithChildren & {
    title?: string
    drawerToggleLabel?: string
    drawerToggleIconName?: string
    className?: string
    headerContent?: JSX.Element
    isOpen: boolean
    setIsOpen: Dispatch<SetStateAction<boolean>>
    position?: 'left' | 'right'
    toggleButtonRef?: React.Ref<HTMLButtonElement>
    titleDataWalkthrough?: string
}

export const Drawer: React.FC<DrawerProps> = (props) => {
    const {
        title,
        drawerToggleLabel,
        drawerToggleIconName,
        headerContent,
        position = 'left',
        isOpen = false,
        setIsOpen,
        toggleButtonRef,
        titleDataWalkthrough,
    } = props
    const scrollableRef = useRef<HTMLDivElement>(null)
    const isScrolled = useIsScrolled(scrollableRef)

    const className = classnames(
        'group/drawer transition-[width] ease-out duration-300 relative h-full shrink-0',
        {
            'w-drawerWidth': isOpen,
            'w-[40px]': !isOpen,
            'is-open': isOpen,
            'is-closed': !isOpen,
        },
        position === 'right' && 'order-last',
        props.className
    )

    const headerClassName = classnames(
        'flex items-center justify-between gap-2 px-6 py-6 relative z-30',
        {
            'border-b border-b-neutral-200': isScrolled,
        }
    )

    return (
        <div
            className={className}
            role="dialog"
            aria-labelledby="drawer-title"
            aria-hidden={!isOpen}
            data-testid="drawer"
            data-walkthrough={titleDataWalkthrough}
        >
            <div
                className={classnames(
                    'absolute top-0 h-full w-drawerWidth',
                    position === 'left'
                        ? 'right-0 border-r'
                        : 'left-0 border-l',
                    'border-neutral-200'
                )}
            >
                <div
                    className={classnames('absolute z-40', {
                        'left-full top-6 -ml-4': position === 'left',
                        'right-full top-4 -mr-4 mt-2': position === 'right',
                        'w-max': !isOpen && position === 'left',
                    })}
                >
                    <DrawerToggleButton
                        isOpen={isOpen}
                        label={drawerToggleLabel}
                        iconName={drawerToggleIconName}
                        onClick={() => {
                            setIsOpen((isOpen) => !isOpen)
                        }}
                        position={position}
                        toggleButtonRef={toggleButtonRef}
                    />
                </div>
                <div className="h-full transition-all group-[.is-closed]/drawer:opacity-0">
                    <div className={headerClassName}>
                        <div
                            className="text-heading-6 mr-auto flex h-8 items-center"
                            data-testid="drawer-title"
                        >
                            {title}
                        </div>
                        {headerContent}
                    </div>
                    <div
                        data-testid="drawer-content"
                        className={classnames(
                            'l-0 absolute bottom-0 top-[80px] w-full px-6 pb-5',
                            position === 'left'
                                ? 'overflow-y-scroll'
                                : 'overflow-y-visible'
                        )}
                        ref={scrollableRef}
                    >
                        {props.children}
                    </div>
                </div>
            </div>
        </div>
    )
}
type DrawerToggleButtonProps = {
    onClick: MouseEventHandler
    isOpen: boolean
    label?: string
    iconName?: string
    position?: 'left' | 'right'
    toggleButtonRef?: React.Ref<HTMLButtonElement>
}

const DrawerToggleButton: React.FC<DrawerToggleButtonProps> = (props) => {
    const {
        onClick,
        isOpen,
        label,
        iconName,
        position = 'left',
        toggleButtonRef,
    } = props

    const buttonClassname = classnames(
        'border outline-none border-neutral-200 rounded-lg group-[.is-closed]/drawer h-8 px-2 bg-white',
        'flex items-center'
    )

    const labelClassname = classnames(
        'grid grid-cols-[1fr] transition-all',
        'group-[.is-open]/drawer:grid-cols-[0fr]',
        'group-[.is-open]/drawer:opacity-0'
    )

    return (
        <button
            className={buttonClassname}
            onClick={onClick}
            data-testid="drawer-toggle-button"
            ref={toggleButtonRef}
        >
            {position === 'left' && label && iconName && (
                <div className={labelClassname}>
                    <div className="overflow-hidden">
                        <div className="flex items-center gap-2 pr-1">
                            <Icon name={iconName} size="small" />
                            <span className="text-label-small whitespace-nowrap">
                                {label}
                            </span>
                        </div>
                    </div>
                </div>
            )}
            <Icon
                name={
                    position === 'right'
                        ? isOpen
                            ? 'chevron_right'
                            : 'chevron_left'
                        : isOpen
                          ? 'chevron_left'
                          : 'chevron_right'
                }
                size="small"
            />
        </button>
    )
}

type DrawerWrapperProps = {
    className?: string
} & PropsWithChildren

export const DrawerWrapper: React.FC<DrawerWrapperProps> = (props) => {
    const { children } = props
    const className = classnames('flex', props.className)

    return <div className={className}>{children}</div>
}
